import Empty from "../../../views/empty"
import Node from "../../../views/nodes/node"
import End from "../../../views/nodes/end"
import Layout from "../../../views/graph/layout"

/**
 * Convenience function to simplify `renderNodes`
 *
 * @param nodeData {object} data of individual node from Workflow::Graph.as_json
 * @param data {object} { roots: [JSON], nodes: [JSON], edges: [JSON], scope: HTMLElement, layout: null }
 * @return node {HTMLElement} appropriate HTML element for nodeData.kind
 */
const createNode = (nodeData, data) => {
  const existingNode = data.scope.querySelector(
    `workflow-node[data-oid='${nodeData.id}']`
  )

  if (existingNode) {
    return existingNode
  }

  const node = buildNode(nodeData)

  node.compute(data.roots, nodeData)

  data.scope.appendChild(node)

  return node
}

/**
 * Convenience function to simplify `createNode`
 *
 * @param nodeData {object} data of individual node from Workflow::Graph.as_json
 * @return {HTMLElement} appropriate HTML element for nodeData.kind
 */
const buildNode = nodeData => {
  switch (nodeData.kind) {
    case "empty":
      return new Empty()
    case "end":
      return new End(nodeData)
    default:
      return new Node()
  }
}

/**
 * Modifies a workflow node for placement in the graph layout/DOM.
 * @param {object} node Workflow node from Workflow::Graph.as_json
 * @param {*} data The full workflow graph data
 * @param {(node, data) => HTMLElement} factory Function that creates the actual HTML element
 * @returns
 */
export const renderNode = (node, data, factory) => {
  const element = factory(node, data)
  const sourceNodes = node.sources
    ? node.sources.map(id => data.nodes.find(n => n.id == id))
    : []

  const maxNodeWidth = node.kind == "end" ? 150 : Layout.maxNodeWidth

  return {
    width: element.layoutWidth || Math.min(element.clientWidth, maxNodeWidth),
    height: element.layoutHeight || element.clientHeight,
    element: element,
    sourceNodes: sourceNodes,
    ...node
  }
}

/**
 * Iterates over an array of JSON nodes produced from Workflow::Graph.as_json
 * and returns each modified with attributes needed to ultimately place them in
 * the DOM. Example attributes added:
 *  - element {HTMLElement} the actual DOM node to ultimately be rendered on screen.
 *  - width {number} the client width of the element
 *  - height {number} the client height of the elemtn
 *  - sourceNodes {array} source node ids
 *
 * @param data {object} { roots: [JSON], nodes: [JSON], edges: [JSON], scope: HTMLElement, layout: null }
 * @return {object} { roots: [JSON], nodes: [JSON], edges: [JSON], scope: HTMLElement, layout: null }
 */
const renderNodes = data => {
  const nodes = data.nodes.map(node => renderNode(node, data, createNode))

  return {
    ...data,
    nodes: nodes
  }
}

export default renderNodes
